import { Injectable } from '@angular/core';
import { Observable, defer, throwError, timer } from 'rxjs';
import { catchError, map, retryWhen, mergeMap } from 'rxjs/operators';
import { CapacitorHttp, HttpResponse } from '@capacitor/core';
import { environment } from 'src/environments/environment';

/* ===================== TYPES ===================== */

export interface ComfortCalculationRequest {
  windowConfiguration: any;
  te: number;
  ti: number;
  phi: number;
}

export interface ComfortCalculationResponse {
  surfaceTemperature: number;
  dewPoint: number;
  moldTemperature: number;
}

/* ===================== SERVICE ===================== */

@Injectable({ providedIn: 'root' })
export class ComfortApiService {
  private readonly apiUrl =
    environment.caluwin_api.base_url +
    environment.caluwin_api.endpoints.comfort_calculator;

  private readonly RETRY_COUNT = 2;
  private readonly BASE_DELAY_MS = 200;

  constructor() { }

  /* ===================== PUBLIC API ===================== */

  calculateComfort(
    params: Partial<ComfortCalculationRequest>
  ): Observable<ComfortCalculationResponse> {
    const requestBody = this.buildRequestBody(params);

    return defer(() =>
      CapacitorHttp.post({
        url: this.apiUrl,
        method: 'POST',
        headers: this.getHeaders(),
        data: requestBody,
      })
    ).pipe(
      map(this.handleSuccessResponse),
      retryWhen(this.retryWithBackoff()),
      catchError(err => throwError(() => this.handleFinalError(err)))
    );
  }

  /* ===================== HELPERS ===================== */

  private buildRequestBody(
    params: Partial<ComfortCalculationRequest>
  ): ComfortCalculationRequest {
    return {
      windowConfiguration: params.windowConfiguration ?? {},
      te: params.te ?? -5,
      ti: params.ti ?? 20,
      phi: params.phi ?? 50,
    };
  }


  private getHeaders(): Record<string, string> {
    return {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    };
  }

  private handleSuccessResponse(
    response: HttpResponse
  ): ComfortCalculationResponse {
    if (response.status < 200 || response.status >= 300) {
      throw response;
    }
    return response.data as ComfortCalculationResponse;
  }

  /* ===================== RETRY LOGIC ===================== */

  private retryWithBackoff() {
    return (errors: Observable<any>) =>
      errors.pipe(
        mergeMap((error, retryIndex) => {
          const attempt = retryIndex + 1;

          if (
            attempt > this.RETRY_COUNT ||
            !this.isRetryableError(error?.status)
          ) {
            return throwError(() => error);
          }

          const delayTime = Math.pow(2, attempt) * this.BASE_DELAY_MS;
          return timer(delayTime);
        })
      );
  }

  private isRetryableError(status?: number): boolean {
    return [0, 408, 429, 500, 502, 503, 504].includes(status ?? 0);
  }

  /* ===================== ERROR HANDLING ===================== */

  private handleFinalError(error: any): Error {
    if (typeof error?.status === 'number') {
      return this.handleHttpError(error.status, error.data);
    }
    return this.handleNetworkError(error);
  }

  private handleHttpError(status: number, data: any): Error {
    const messageMap: Record<number, string> = {
      400: 'Invalid request parameters.',
      401: 'Unauthorized. Please login again.',
      403: 'Access forbidden.',
      404: 'API endpoint not found.',
      422: 'Validation error. Please check your inputs.',
      429: 'Too many requests. Please try again later.',
      500: 'Server error. Please try again later.',
      502: 'Service temporarily unavailable.',
      503: 'Service temporarily unavailable.',
      504: 'Service temporarily unavailable.',
    };

    const message = data?.message || messageMap[status] || `Error ${status}`;
    console.error('API Error:', status, data);
    return new Error(message);
  }

  private handleNetworkError(error: any): Error {
    const message =
      error?.message?.includes('timeout')
        ? 'Request timeout. Please try again.'
        : 'Network error. Please check your connection.';

    console.error('Network Error:', error);
    return new Error(message);
  }
}
